import { Injectable, signal, computed, effect, inject, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';
import { CustomerAuthService } from './auth.service';
import { AccountService } from './account.service';

export interface CartItem {
  slug:          string;
  name:          string;
  category:      string;
  price:         number;
  originalPrice: number;
  img:           string;
  size:          string;
  qty:           number;
  isVoucher?:    boolean;
  voucher?:      { occasion: string; recipientName: string; recipientEmail: string; message: string };
}

export interface DeliveryAddress {
  flat: string; street: string; landmark: string;
  pincode: string; city: string; country: string; state: string;
  name: string; phone: string;
  saveAs: 'Home' | 'Work' | 'Other'; isDefault: boolean;
}

export interface AppliedCoupon {
  code: string;
  discount: number;
  kind: 'coupon' | 'voucher';
}

const GUEST_KEY = 'mmtess_guest_cart';
const DESEL_KEY = 'mmtess_cart_deselected';

/**
 * Cart with backend persistence. While signed out, the cart lives in this signal + localStorage
 * (guest cart). On sign-in the guest cart is MERGED into the server cart (My Cart `/cart`), and
 * every change is pushed to the server thereafter. On sign-out the in-memory cart is cleared
 * (the server copy remains for next sign-in).
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  private toast = inject(ToastService);
  private http = inject(HttpClient);
  private auth = inject(CustomerAuthService);
  private account = inject(AccountService);
  private url = `${environment.customApiBase}/cart`;
  private _items = signal<CartItem[]>([]);
  private prevAuthed = false;

  // Selected delivery address (set on the ADDRESS step, read by cart + checkout)
  readonly deliveryAddress = signal<DeliveryAddress | null>(null);

  /**
   * Pre-fill the delivery address from the customer's default (or first) saved address when none
   * has been chosen yet — so a returning customer sees their address on the cart immediately
   * instead of "Please select address". Never overrides an address the user explicitly picked.
   */
  hydrateDefaultAddress(): void {
    if (!this.auth.isAuthenticated() || this.deliveryAddress()) return;
    this.account.listAddresses().subscribe({
      next: (list) => {
        if (this.deliveryAddress() || !list?.length) return;
        const a = list.find((x) => x.isDefault) ?? list[0];
        this.deliveryAddress.set({
          flat: a.flat ?? '', street: a.street ?? '', landmark: a.landmark ?? '',
          pincode: a.pincode ?? '', city: a.city ?? '', country: a.country ?? 'India', state: a.state ?? '',
          name: a.name ?? '', phone: a.phone ?? '',
          saveAs: (a.saveAs as DeliveryAddress['saveAs']) ?? 'Home', isDefault: !!a.isDefault,
        });
      },
      error: () => {},
    });
  }

  // Applied coupon/voucher — set in the cart, read by cart + checkout, sent to Place Order.
  readonly appliedCoupon = signal<AppliedCoupon | null>(null);
  clearCoupon(): void { this.appliedCoupon.set(null); }

  /**
   * Re-validate the applied coupon against the CURRENT selected subtotal so the displayed discount
   * always matches what Place Order will recompute server-side. Clears the coupon if it's no longer
   * valid (e.g. cart dropped below the minimum). No-op when no coupon is applied.
   */
  private revalidateCoupon(): void {
    const code = this.appliedCoupon()?.code;
    if (!code) return;
    const subtotal = this.selectedSubtotal();
    this.http.post<any>(`${environment.customApiBase}/apply-coupon`, { code, subtotal }).subscribe({
      next: (r) => {
        const d = (r && r.success !== false && r.data) ? r.data : null;
        if (d && Number(d.discount) >= 0) this.appliedCoupon.set({ code: d.code, discount: d.discount, kind: d.kind });
        else { this.appliedCoupon.set(null); this.toast.show('Coupon removed — no longer valid for your cart.', 'error'); }
      },
      error: () => { this.appliedCoupon.set(null); this.toast.show('Coupon removed — no longer valid for your cart.', 'error'); },
    });
  }

  // Membership add-on + wallet/reward-points redemption (M9) — checkout-time choices.
  readonly addMembership = signal(false);
  // Selected membership plan + its price, so the cart reflects the plan the user actually picked
  // (3 months ₹149 vs 12 months ₹199) instead of a hard-coded fee.
  readonly membershipPlan = signal<'3mo' | '12mo'>('12mo');
  readonly membershipPrice = computed(() => (this.membershipPlan() === '3mo' ? 149 : 199));
  // Human-readable plan label sent to the server so it charges/grants the plan the user actually
  // picked (backend maps '3 months'→₹149/90d, '12 months'→₹199/365d) instead of hardcoding 12 months.
  readonly membershipPlanLabel = computed(() => (this.membershipPlan() === '3mo' ? '3 months' : '12 months'));
  setMembership(v: boolean, plan?: '3mo' | '12mo'): void {
    this.addMembership.set(v);
    if (plan) this.membershipPlan.set(plan);
  }
  readonly walletToUse = signal(0);   // ₹ of wallet balance to redeem
  readonly pointsToUse = signal(0);   // reward points to redeem (1 pt = ₹1)

  readonly items    = this._items.asReadonly();
  readonly count    = computed(() => this._items().reduce((s, i) => s + i.qty, 0));
  readonly subtotal = computed(() => this._items().reduce((s, i) => s + i.price * i.qty, 0));

  // ── Member pricing (TSS model) ──────────────────────────────────────────────
  // The discounted (member) price applies ONLY to members — or to anyone who adds the
  // membership to this order (buying membership unlocks member pricing on the same cart).
  // Non-members are charged the regular MRP (originalPrice); the member price is a teaser.
  readonly isMemberPricing = computed(() => !!this.auth.user()?.membership?.isMember || this.addMembership());
  /** Regular (MRP) price a non-member pays — falls back to price when there's no separate MRP. */
  static regularOf(i: CartItem): number { return i.originalPrice > i.price ? i.originalPrice : i.price; }
  /** MRP subtotal of selected items (what a non-member pays for the items). */
  readonly mrpSubtotal   = computed(() => this.selectedItems().reduce((s, i) => s + CartService.regularOf(i) * i.qty, 0));
  /** Potential member savings on selected items (realized only when isMemberPricing). */
  readonly memberSavings = computed(() => this.selectedItems().reduce((s, i) => s + (CartService.regularOf(i) - i.price) * i.qty, 0));
  /** Actual item charge: member price when member-priced, else MRP. */
  readonly itemsPayable  = computed(() => this.isMemberPricing() ? this.mrpSubtotal() - this.memberSavings() : this.mrpSubtotal());

  // ── Per-item selection (opt-OUT: an item is billed unless explicitly deselected) ──
  // Persisted to localStorage so the selection survives a page refresh between cart → checkout.
  static keyOf(i: { slug: string; size: string }): string { return `${i.slug}|${i.size}`; }
  private _deselected = signal<Set<string>>(this.loadDeselected());
  private loadDeselected(): Set<string> {
    try { const raw = localStorage.getItem(DESEL_KEY); return new Set(raw ? JSON.parse(raw) as string[] : []); } catch { return new Set(); }
  }
  private setDeselected(s: Set<string>): void {
    this._deselected.set(s);
    try { localStorage.setItem(DESEL_KEY, JSON.stringify([...s])); } catch { /* ignore quota */ }
  }
  isSelected(i: CartItem): boolean { return !this._deselected().has(CartService.keyOf(i)); }
  toggleSelected(i: CartItem): void {
    const k = CartService.keyOf(i);
    const s = new Set(this._deselected());
    s.has(k) ? s.delete(k) : s.add(k);
    this.setDeselected(s);
    this.revalidateCoupon();
  }
  readonly selectedItems = computed(() => this._items().filter(i => !this._deselected().has(CartService.keyOf(i))));
  // Coupon base — must mirror the server's `chargeOf`: members are billed the member price, non-members
  // the regular MRP. Summing member prices for everyone made the shown discount / min-order gate diverge
  // from what Place Order actually applies (backend re-computes on the MRP subtotal for non-members).
  readonly selectedSubtotal = computed(() => {
    const member = this.isMemberPricing();
    return this.selectedItems().reduce((s, i) => s + (member ? i.price : CartService.regularOf(i)) * i.qty, 0);
  });
  readonly selectedCount = computed(() => this.selectedItems().reduce((s, i) => s + i.qty, 0));
  readonly allSelected = computed(() => this._items().length > 0 && this.selectedItems().length === this._items().length);
  selectedKeys(): string[] { return this.selectedItems().map(i => CartService.keyOf(i)); }
  toggleAllSelected(): void {
    this.setDeselected(this.allSelected() ? new Set(this._items().map(i => CartService.keyOf(i))) : new Set());
    this.revalidateCoupon();
  }

  constructor() {
    this.prevAuthed = this.auth.isAuthenticated();
    if (!this.prevAuthed) this.restoreGuest();

    // React to login/logout transitions (tracks only the auth signal).
    effect(() => {
      const authed = this.auth.isAuthenticated();
      untracked(() => {
        if (authed && !this.prevAuthed) this.onLogin();
        else if (!authed && this.prevAuthed) this.onLogout();
        this.prevAuthed = authed;
      });
    });

    // Already signed in at startup (e.g. page refresh) → load/merge the server cart + address.
    if (this.prevAuthed) { this.onLogin(); this.hydrateDefaultAddress(); }
  }

  updateSize(slug: string, oldSize: string, newSize: string) {
    this._items.update(list =>
      list.map(i => (i.slug === slug && i.size === oldSize ? { ...i, size: newSize } : i)),
    );
    this.persist();
  }

  add(item: Omit<CartItem, 'qty'>, qty = 1) {
    this._items.update(list => {
      const idx = list.findIndex(i => i.slug === item.slug && i.size === item.size);
      if (idx >= 0) {
        const copy = [...list];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...list, { ...item, qty }];
    });
    this.persist();
    this.revalidateCoupon();
    this.toast.show('Product added to your cart successfully.');
  }

  updateQty(slug: string, size: string, qty: number) {
    if (qty <= 0) { this.remove(slug, size); return; }
    this._items.update(list =>
      list.map(i => i.slug === slug && i.size === size ? { ...i, qty } : i)
    );
    this.persist();
    this.revalidateCoupon(); // qty change can change the discount the backend will honour
  }

  remove(slug: string, size: string) {
    this._items.update(list => list.filter(i => !(i.slug === slug && i.size === size)));
    this.persist();
    this.revalidateCoupon();
  }

  clear() { this._items.set([]); this.appliedCoupon.set(null); this.setDeselected(new Set()); this.resetLoyalty(); this.persist(); }

  private resetLoyalty() { this.addMembership.set(false); this.walletToUse.set(0); this.pointsToUse.set(0); }

  // ── persistence ────────────────────────────────────────────────────────────
  private persist() {
    const items = this._items();
    if (this.auth.isAuthenticated()) {
      this.http.post(this.url, { op: 'replace', items: this.toApi(items) }).subscribe({ error: () => {} });
    } else {
      try { localStorage.setItem(GUEST_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
    }
  }

  private onLogin() {
    // Fold the guest cart into the server cart, then adopt the merged result.
    this.http.post<any>(this.url, { op: 'merge', items: this.toApi(this._items()) }).subscribe({
      next: (res) => {
        const cart = res?.data ?? res;
        this._items.set(this.fromApi(cart?.items ?? []));
        this.revalidateCoupon(); // merged subtotal may differ from when the coupon was applied
        this.hydrateDefaultAddress(); // pre-fill the saved default address on sign-in
        try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
      },
      error: () => {},
    });
  }

  private onLogout() {
    this._items.set([]);
    this.appliedCoupon.set(null);
    this.deliveryAddress.set(null);
    this.setDeselected(new Set());
    this.resetLoyalty();
    try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
  }

  private restoreGuest() {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      if (raw) this._items.set(JSON.parse(raw) as CartItem[]);
    } catch { /* ignore corrupt cache */ }
  }

  private toApi(items: CartItem[]): unknown[] {
    return items.map(i => ({
      slug: i.slug, name: i.name, category: i.category, img: i.img,
      size: i.size, price: i.price, originalPrice: i.originalPrice, qty: i.qty,
      ...(i.isVoucher ? { isVoucher: true, voucher: i.voucher } : {}),
    }));
  }

  private fromApi(items: any[]): CartItem[] {
    return (items ?? []).map(i => ({
      slug: i.slug, name: i.name, category: i.category,
      price: i.price ?? 0, originalPrice: i.originalPrice ?? i.price ?? 0,
      img: i.img, size: i.size ?? '', qty: Math.max(1, Number(i.qty) || 1),
      ...(i.isVoucher ? { isVoucher: true, voucher: i.voucher } : {}),
    }));
  }
}
